
import { useState, useEffect } from 'react';
import { musicApiService, Track } from '../../../services/musicApi';

interface TrendingTracksProps {
  onTrackSelect: (track: any) => void;
}

export default function TrendingTracks({ onTrackSelect }: TrendingTracksProps) {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTracks = async () => {
      try {
        const popularTracks = await musicApiService.getPopularTracks(10);
        setTracks(popularTracks);
      } catch (error) {
        console.error('Error loading trending tracks:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTracks();
  }, []);

  const handlePlayTrack = (track: Track) => {
    const formattedTrack = {
      id: track.id,
      title: track.name,
      artist: track.artist,
      album: track.album,
      duration: track.duration,
      audioUrl: track.audioUrl,
      imageUrl: track.imageUrl
    };
    onTrackSelect(formattedTrack);
  };

  if (loading) {
    return (
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Trending Now</h2>
          </div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg animate-pulse">
                <div className="w-16 h-16 bg-gray-300 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-300 rounded w-1/3 mb-2"></div>
                  <div className="h-3 bg-gray-300 rounded w-1/4"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Trending Now</h2>
          <button className="text-purple-600 hover:text-purple-700 font-medium cursor-pointer">
            View Chart
          </button>
        </div>

        <div className="space-y-2">
          {tracks.map((track, index) => (
            <div
              key={track.id}
              className="group flex items-center space-x-4 p-4 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-center w-8 text-lg font-bold text-gray-400 group-hover:hidden">
                {index + 1}
              </div>
              <button
                onClick={() => handlePlayTrack(track)}
                className="hidden group-hover:flex items-center justify-center w-8 h-8 bg-purple-600 hover:bg-purple-700 text-white rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-play-fill text-sm"></i>
              </button>

              <img
                src={track.imageUrl || 'https://readdy.ai/api/search-image?query=music%20album%20cover%20with%20modern%20abstract%20design%2C%20vibrant%20colors%2C%20artistic%20style&width=200&height=200&seq=trending-cover&orientation=squarish'}
                alt={track.name}
                className="w-16 h-16 rounded-lg object-cover object-top"
              />

              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 truncate">{track.name}</h3>
                <p className="text-sm text-gray-600 truncate">{track.artist}</p>
              </div>

              <div className="hidden md:block text-sm text-gray-500">
                {track.album}
              </div>

              <div className="flex items-center space-x-2">
                <button className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer">
                  <i className="ri-heart-line text-lg"></i>
                </button>
                <span className="text-sm text-gray-500 w-12 text-right">
                  {musicApiService.formatDuration(track.duration)}
                </span>
                <button className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                  <i className="ri-more-2-fill text-lg"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
